import Breadcrumbs from "components/Common/Breadcrumb";
import {
  useInitStatement,
  usePrepareStatement,
} from "hooks/invoiceStatement/useInvoiceStatement";
import React, { useRef, useState } from "react";
import { MetaTags } from "react-meta-tags";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { EMPTY_PREPARE_RESULT, PREPARE_PER_PAGE } from "../constants";
import {
  buildPrepareParams,
  normalizePrepareResponse,
  resolvePagingChange,
} from "../helper";
import { buildInitPayload } from "../statementHelper";
import { useStatementSelection } from "../useStatementSelection";
import StepSelectInvoice from "./StepSelectInvoice";
import StepViewStatement from "./StepViewStatement";

const PAGE_TITLE = "Lập bảng kê hóa đơn thay thế /điều chỉnh";

const STEPS = [
  { id: 1, label: "Chọn HĐ sai" },
  { id: 2, label: "Xem bảng kê" },
];

const CreateStatement = () => {
  const { addToast } = useToasts();
  const history = useHistory();

  // Phạm vi chi nhánh/đơn vị đang chọn trên header
  const Dashboard = useSelector(state => state.Dashboard);
  const bucode = Dashboard?.finalBuInfo?.code;

  const [step, setStep] = useState(1);
  // Điều kiện của lần tìm đã ra kết quả, để hiện lại kèm danh sách
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [searched, setSearched] = useState(false);
  const [statementId, setStatementId] = useState(null);
  // Trang và số dòng/trang do màn hình làm chủ, không phụ thuộc backend có echo lại hay không
  const [paging, setPaging] = useState({
    page: 1,
    perPage: PREPARE_PER_PAGE,
  });
  const pendingFilters = useRef(null);
  // Đổi trang thì giữ nguyên lựa chọn, chỉ tìm mới mới xóa
  const isNewSearch = useRef(true);
  const [result, setResult] = useState(EMPTY_PREPARE_RESULT);

  const { selectedIds, selectedRows, select, selectAll, clear } =
    useStatementSelection();

  const mutation = usePrepareStatement({
    onSuccess: payload => {
      const raw = payload?.data;

      // HTTP 200 nhưng nghiệp vụ thất bại thì useCRUD không bắt được
      if (raw?.result === false) {
        addToast(raw?.message || "Không tải được danh sách hóa đơn", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      if (isNewSearch.current) {
        clear();
      }
      setResult(normalizePrepareResponse(raw, PREPARE_PER_PAGE));
      setAppliedFilters(pendingFilters.current);
      setSearched(true);
    },
  });

  const runSearch = (values, page, perPage) => {
    setPaging({ page, perPage });
    mutation.mutate(buildPrepareParams({ ...values, page, perPage, bucode }));
  };

  const initMutation = useInitStatement({
    onSuccess: payload => {
      const raw = payload?.data;
      if (raw?.result === false || !raw?.statement_id) {
        addToast(raw?.message || "Không lập được bảng kê", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }
      setStatementId(raw.statement_id);
      setStep(2);
    },
  });

  const handleCreate = () => {
    initMutation.mutate(buildInitPayload(appliedFilters, selectedRows));
  };

  const handleSearch = values => {
    // Chỉ hiện lại điều kiện khi request thành công, nên giữ tạm ở ref.
    // Chụp luôn bên bán tại thời điểm tìm, để đổi chi nhánh trên header sau đó
    // thì phần hiển thị vẫn khớp với dữ liệu đang nằm trên bảng.
    pendingFilters.current = {
      ...values,
      sellerName: Dashboard?.finalBuInfo?.ten || "",
    };
    isNewSearch.current = true;
    runSearch(pendingFilters.current, 1, paging.perPage);
  };

  // Phân trang phía server: mỗi lần đổi trang hoặc đổi số dòng/trang là một
  // lượt gọi API mới. Đổi số dòng/trang thì phải quay về trang 1 vì số trang
  // đã khác hẳn, giữ nguyên trang cũ dễ rơi ra ngoài phạm vi.
  const handlePageChange = (page, sizePerPage) => {
    if (!pendingFilters.current) return;
    const next = resolvePagingChange(paging, page, sizePerPage);
    isNewSearch.current = false;
    runSearch(pendingFilters.current, next.page, next.perPage);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{PAGE_TITLE}</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs title="Hóa đơn" breadcrumbItem={PAGE_TITLE} />

          <div className="d-flex align-items-center mb-3">
            {STEPS.map((item, index) => {
              const isActive = step === item.id;
              const isDone = step > item.id;
              return (
                <React.Fragment key={item.id}>
                  {index > 0 && (
                    <div
                      style={{
                        width: 32,
                        height: 2,
                        background: isDone || isActive ? "#556ee6" : "#e9ecef",
                      }}
                    />
                  )}
                  <button
                    type="button"
                    // Chỉ cho quay lại bước đã qua, không cho nhảy tới
                    disabled={!isDone}
                    onClick={() => setStep(item.id)}
                    className="btn d-flex align-items-center gap-2 px-3 py-1 border-0 font-size-13"
                    style={{
                      background: isActive
                        ? "#556ee6"
                        : isDone
                        ? "rgba(85, 110, 230, 0.12)"
                        : "#f1f3f7",
                      color: isActive ? "#fff" : isDone ? "#556ee6" : "#8a92a6",
                      cursor: isDone ? "pointer" : "default",
                      opacity: 1,
                      borderRadius: 6,
                    }}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-circle fw-bold font-size-11"
                      style={{
                        width: 18,
                        height: 18,
                        background: isActive
                          ? "rgba(255, 255, 255, 0.25)"
                          : isDone
                          ? "rgba(85, 110, 230, 0.2)"
                          : "#e2e5ec",
                      }}
                    >
                      {isDone ? <i className="bx bx-check" /> : item.id}
                    </span>
                    <span className="fw-medium">{item.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>

          {step === 1 ? (
            <StepSelectInvoice
              result={result}
              appliedFilters={appliedFilters}
              searched={searched}
              isLoading={mutation.isLoading}
              selectedIds={selectedIds}
              page={paging.page}
              sizePerPage={paging.perPage}
              onSearch={handleSearch}
              onSelect={select}
              onSelectAll={selectAll}
              onPageChange={handlePageChange}
              onClearSelection={clear}
              onCreate={handleCreate}
              isCreating={initMutation.isLoading}
            />
          ) : (
            <StepViewStatement
              statementId={statementId}
              onBack={() => setStep(1)}
              // Ký xong thì về màn danh sách bảng kê
              onSigned={() => history.push("/invoice-statements")}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default CreateStatement;
